import React, { useEffect, useState } from 'react';
import { Box, Button, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import axios from 'axios';
import {
  EmptyState,
  FormDialog,
  LoadingState,
  MetricCard,
  PageHeader,
  ResponsiveTable,
  SectionCard,
  StatusChip,
  useFeedback,
} from '../components/ui';
import { describeGeolocationError, getBrowserPosition } from '../utils/geolocation';

import { API_BASE_URL } from '../config/apiBase';

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(date);
}

function punchErrorMessage(error) {
  if (!error?.response) {
    if (error?.code === 'unsupported' || error?.code === 'insecure' || error?.code === 1 || error?.code === 2 || error?.code === 3) {
      return describeGeolocationError(error);
    }
    return error?.message && !String(error.message).includes('Network Error')
      ? describeGeolocationError(error)
      : 'Falha de conexão. O ponto não foi registrado.';
  }
  if (error.response.status === 401) return 'Sessão expirada. Entre novamente.';
  return error.response.data?.message || 'Não foi possível registrar o ponto.';
}

function geofenceLabel(status) {
  if (status === 'not_configured') return 'Cerca não configurada';
  if (status === 'inside') return 'Dentro da área';
  if (status === 'outside') return 'Fora da área';
  return status || '—';
}

export default function PointRegister() {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const { notify } = useFeedback();
  const [loadingData, setLoadingData] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [estatisticas, setEstatisticas] = useState(null);
  const [statsError, setStatsError] = useState(false);
  const [historyError, setHistoryError] = useState(false);
  const [ultimoPonto, setUltimoPonto] = useState(null);
  const [observacoes, setObservacoes] = useState('');
  const [notesOpen, setNotesOpen] = useState(false);
  const [reading, setReading] = useState(null);
  const [readingError, setReadingError] = useState('');
  const [clock, setClock] = useState(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => setClock(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const carregar = async () => {
    setLoadingData(true);
    const headers = { headers: authHeaders() };
    const [historyResult, statsResult] = await Promise.allSettled([
      axios.get(`${API_BASE_URL}/ponto/historico`, headers),
      axios.get(`${API_BASE_URL}/ponto/estatisticas`, headers),
    ]);
    if (historyResult.status === 'fulfilled' && Array.isArray(historyResult.value.data)) {
      setHistorico(historyResult.value.data);
      setUltimoPonto(historyResult.value.data[0] || null);
      setHistoryError(false);
    } else {
      setHistorico([]);
      setUltimoPonto(null);
      setHistoryError(true);
    }
    if (statsResult.status === 'fulfilled' && statsResult.value.data) {
      setEstatisticas(statsResult.value.data);
      setStatsError(false);
    } else {
      setEstatisticas(null);
      setStatsError(true);
    }
    setLoadingData(false);
  };

  useEffect(() => {
    carregar();
  }, []);

  const lerLocalizacao = async () => {
    setReadingError('');
    try {
      const position = await getBrowserPosition();
      setReading(position);
      return position;
    } catch (error) {
      setReading(null);
      const message = describeGeolocationError(error);
      setReadingError(message);
      notify(message, 'error');
      return null;
    }
  };

  const registrarPonto = async (tipo) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const localizacao = await getBrowserPosition();
      setReading(localizacao);
      const response = await axios.post(`${API_BASE_URL}/ponto/registrar`, {
        tipo,
        latitude: localizacao.latitude,
        longitude: localizacao.longitude,
        accuracy: localizacao.accuracy,
        captured_at: localizacao.captured_at,
        observacoes,
      }, { headers: authHeaders() });
      notify(response.data?.message || 'Ponto registrado.', response.data?.geofence_status === 'not_configured' ? 'info' : 'success');
      setObservacoes('');
      await carregar();
    } catch (error) {
      notify(punchErrorMessage(error), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const podeEntrada = !ultimoPonto || ultimoPonto.tipo === 'saida';
  const podeSaida = Boolean(ultimoPonto && ultimoPonto.tipo === 'entrada');
  const metric = (value) => (value == null || value === '' ? '—' : value);

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        title="Registro de ponto"
        subtitle="A marcação só é confirmada depois da resposta do servidor. A precisão do aparelho é uma estimativa."
      />

      <SectionCard title="Agora" sx={{ mb: 3 }}>
        <Typography variant="h2" component="p">{clock.toLocaleTimeString('pt-BR')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {ultimoPonto
            ? `Último registro: ${ultimoPonto.tipo === 'saida' ? 'Saída' : 'Entrada'} em ${formatDateTime(ultimoPonto.data_hora)}`
            : 'Nenhum registro anterior nesta conta.'}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          <Button variant="contained" disabled={submitting || !podeEntrada} onClick={() => registrarPonto('entrada')}>
            {submitting ? 'Registrando...' : 'Registrar entrada'}
          </Button>
          <Button variant="outlined" disabled={submitting || !podeSaida} onClick={() => registrarPonto('saida')}>
            {submitting ? 'Registrando...' : 'Registrar saída'}
          </Button>
          <Button variant="text" disabled={submitting} onClick={lerLocalizacao}>Ler localização</Button>
          <Button variant="text" disabled={submitting} onClick={() => setNotesOpen(true)}>Observação</Button>
        </Box>
        {observacoes ? (
          <Typography variant="body2" sx={{ mt: 1 }}>Observação pronta para o próximo registro.</Typography>
        ) : null}
        {reading ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {`Precisão estimada: ${Math.round(reading.accuracy)} m · leitura ${formatDateTime(reading.captured_at)}`}
          </Typography>
        ) : null}
        {readingError ? <Typography variant="body2" color="error" sx={{ mt: 1 }}>{readingError}</Typography> : null}
      </SectionCard>

      {loadingData ? <LoadingState label="Carregando seus registros" /> : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' }, gap: 2, mb: 3 }}>
            <MetricCard label="Total" value={statsError ? '—' : metric(estatisticas?.total_pontos)} hint={statsError ? 'Falha ao carregar' : 'Conta atual'} />
            <MetricCard label="Aprovados" value={statsError ? '—' : metric(estatisticas?.aprovados)} />
            <MetricCard label="Pendentes" value={statsError ? '—' : metric(estatisticas?.pendentes)} />
            <MetricCard label="Rejeitados" value={statsError ? '—' : metric(estatisticas?.rejeitados)} />
          </Box>

          <SectionCard title="Histórico">
            {historyError ? (
              <EmptyState title="Não foi possível carregar o histórico." action={<Button onClick={carregar}>Tentar novamente</Button>} />
            ) : historico.length === 0 ? (
              <EmptyState title="Nenhum ponto registrado." />
            ) : compact ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {historico.map((ponto) => (
                  <Box key={ponto.id} sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{ponto.tipo === 'saida' ? 'Saída' : 'Entrada'}</Typography>
                    <Typography variant="caption" color="text.secondary">{formatDateTime(ponto.data_hora)}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {ponto.accuracy_m != null ? `Precisão ${ponto.accuracy_m} m` : 'Precisão não informada'}
                      {ponto.geofence_status ? ` · ${geofenceLabel(ponto.geofence_status)}` : ''}
                    </Typography>
                    <StatusChip status={ponto.status} label={ponto.status} />
                  </Box>
                ))}
              </Box>
            ) : (
              <ResponsiveTable minWidth={760} label="Histórico de ponto">
                <TableHead>
                  <TableRow>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Data/hora</TableCell>
                    <TableCell>Precisão</TableCell>
                    <TableCell>Cerca</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Observações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historico.map((ponto) => (
                    <TableRow key={ponto.id} hover>
                      <TableCell>{ponto.tipo === 'saida' ? 'Saída' : 'Entrada'}</TableCell>
                      <TableCell>{formatDateTime(ponto.data_hora)}</TableCell>
                      <TableCell>{ponto.accuracy_m != null ? `${ponto.accuracy_m} m` : '—'}</TableCell>
                      <TableCell>{geofenceLabel(ponto.geofence_status)}</TableCell>
                      <TableCell><StatusChip status={ponto.status} label={ponto.status} /></TableCell>
                      <TableCell>{ponto.observacoes || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ResponsiveTable>
            )}
          </SectionCard>
        </>
      )}

      <FormDialog
        open={notesOpen}
        title="Observação do próximo registro"
        onClose={() => setNotesOpen(false)}
        onSubmit={() => setNotesOpen(false)}
        submitLabel="Guardar"
      >
        <TextField
          label="Observação"
          value={observacoes}
          onChange={(event) => setObservacoes(event.target.value)}
          multiline
          minRows={3}
          fullWidth
        />
      </FormDialog>
    </Box>
  );
}
